import { ProductView } from '@components/product/ProductView'
import useSWRImmutable from 'swr/immutable'
import { useRouter } from 'next/dist/client/router'
import type { ProductViewProps } from '@components/product/ProductView/ProductView'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS, KK_LOCALE_MAP } from '@model/locales'
import type { Product } from '@model/product'
import type { RecommendationsResult } from '@model/product/ProductSearchResult'
import axios from 'axios'
import { useAppState } from '@context/state'
import { FC, useEffect, useMemo } from 'react'
import { unSlug } from '@utils/slugs'
import { CwsGrid, CwsGridCol, CwsGridRow, CwsSection } from '@components/cws'
import { makeConfig } from '@components/category/HomePageCollection/config'
import type { Catalog } from '@model/pricelist/Catalog'
import { localStorageHelper } from '@utils/localStorageHelper'
import { GUEST_CUSTOMER_HEADER } from '@utils/axios-utils'
import { ProductViewBody } from '@pages/api/register-view'
import { PERCENTAGE_OF_POINT_USED } from '../../constants/product'
import TranslationsLoaderProvider from '@ui/components/TranslationLoader/TranslationsLoader'

export type KeyValue = {
  [key: string]: string | number | boolean | string[] | undefined
}

export const getFetcher = (apiPath: string, bodyData: KeyValue) => {
  return async () => {
    const { data } = await axios.post(`/api/${apiPath}`, bodyData)
    return data
  }
}

export const ProductPage: FC<ProductViewProps> = ({ product, reviews }) => {
  const router = useRouter()

  const guestCustomerId = localStorageHelper.get(GUEST_CUSTOMER_HEADER)
  const { slug, productId, catalogId = 'shop' } = router.query
  const sku = productId && !slug ? product.sku : unSlug(slug as string)

  const {
    state: { customer },
  } = useAppState()

  const { taxExemption: coMultiplier } = customer || { taxExemption: '1' }
  const productWithCOPoint = useMemo(() => {
    if (!customer || !coMultiplier) return product

    return {
      ...product,
      [PERCENTAGE_OF_POINT_USED]: `${+product[PERCENTAGE_OF_POINT_USED] * parseFloat(coMultiplier)}`,
    }
  }, [customer, coMultiplier, product])

  useEffect(() => {
    window.product = product
  }, [product])

  const currentCustomerId = customer?.id || guestCustomerId

  const currentLocale = (router.locale as Locale) || 'en'
  const languageId = KK_LANG_IDS[currentLocale]

  useEffect(() => {
    const data: ProductViewBody = {
      productId: product.id,
      customerId: currentCustomerId,
      languageId,
    }
    axios.post(`/api/register-view`, data).catch(console.warn)
  }, [product, currentCustomerId, languageId])

  makeConfig(languageId, catalogId as Catalog)
  const { data: recommendations } = useSWRImmutable<RecommendationsResult>(
    `/api/recommendations/${router.locale}/${currentCustomerId}/${sku}`,
    getFetcher('recommendations', {
      languageId,
      customerId: currentCustomerId,
      sku,
      catalogId,
    })
  )

  const { data: recentlyViewedProducts } = useSWRImmutable<Product[]>(
    `/api/last-seen-products/${currentCustomerId}/${sku}`,
    getFetcher('last-seen-products', {
      languageId,
      customerId: currentCustomerId,
      catalogId,
    })
  )

  if (!product || !sku) {
    return null
  }

  return (
    <TranslationsLoaderProvider>
          <CwsGridRow noGutter>
            <CwsGridCol col="12">
              <ProductView
                product={productWithCOPoint}
                reviews={reviews}
                recommendations={recommendations || { productArray: [] }}
                recentlyViewedProducts={recentlyViewedProducts || []}
              />
            </CwsGridCol>
          </CwsGridRow>
    </TranslationsLoaderProvider>
  )
}

export default ProductPage
